import { useOptimizedTasks } from './useOptimizedTasks';
import { useTaskLists } from './useTaskLists';
import { FamilyEvent } from './useOptimizedEvents';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Hook pour gérer les tâches liées à un événement spécifique
 */
export function useEventTasks(familyId?: string, eventId?: string, event?: FamilyEvent) {
  // ALWAYS call all hooks unconditionally, regardless of parameters
  const { tasks, createTask, loading, ...otherTaskMethods } = useOptimizedTasks(familyId);
  const { taskLists, createTaskList, loading: taskListsLoading } = useTaskLists(familyId);

  // Safely filter tasks only after all hooks are called
  const eventTaskLists = eventId ? taskLists.filter(list => (list as any).event_id === eventId) : [];
  const eventTaskListIds = eventTaskLists.map(list => list.id);
  
  const eventTasks = eventId ? tasks.filter(task => 
    task.event_id === eventId || 
    (task.list_id && eventTaskListIds.includes(task.list_id))
  ) : [];

  // Debug logs
  console.log('useEventTasks - eventId:', eventId);
  console.log('useEventTasks - all tasks:', tasks);
  console.log('useEventTasks - eventTaskLists:', eventTaskLists);
  console.log('useEventTasks - eventTaskListIds:', eventTaskListIds);
  console.log('useEventTasks - filtered eventTasks:', eventTasks);

  // Créer automatiquement une liste pour l'événement si elle n'existe pas
  const createEventTaskList = async () => {
    if (!event || !familyId) return null;
    
    const listName = event.title;
    
    return await createTaskList(listName, '📅', eventId);
  };

  // Créer une tâche liée à l'événement
  const createEventTask = async (
    title: string, 
    description: string, 
    assignedToList: string[], 
    dueDate?: Date
  ) => {
    if (!eventId || !familyId) return;
    
    // Si aucune liste d'événement n'existe, la créer
    let targetListId = eventTaskLists[0]?.id;
    if (!targetListId) {
      const newList = await createEventTaskList();
      targetListId = newList?.id;
    }

    return await createTask(title, description, assignedToList, dueDate, targetListId, eventId);
  };

  return {
    // Données filtrées pour l'événement
    eventTasks,
    eventTaskLists,
    
    // Méthodes spécifiques à l'événement
    createEventTask,
    createEventTaskList,
    
    // Méthodes générales
    ...otherTaskMethods,
    
    // États de chargement
    loading: loading || taskListsLoading,
  };
}